import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Video, CheckCircle, Clock, Users } from "lucide-react";
import { format } from "date-fns";
import type { Meeting } from "./CreateMeeting";

export default function MeetingPlaceholder() {
  const location = useLocation();
  const navigate = useNavigate();
  const { meeting, devicesVerified } = location.state as { meeting: Meeting; devicesVerified: boolean } || {};
  
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!meeting) {
      navigate("/create-meeting");
      return;
    }

    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [meeting, navigate]);

  if (!meeting) {
    return null;
  }

  const formatElapsedTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-green-500/10">
              <Video className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <CardTitle className="text-3xl">Meeting Started</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Meeting Info */}
          <div className="space-y-4 p-6 bg-muted rounded-lg">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Meeting Title</p>
              <p className="text-xl font-semibold">{meeting.title}</p>
            </div>

            {meeting.description && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="text-sm">{meeting.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Started At
                </p>
                <p className="text-sm font-medium">
                  {format(meeting.dateTime, "p")}
                </p>
              </div>

              {meeting.durationMinutes && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Duration
                  </p>
                  <p className="text-sm font-medium">
                    {meeting.durationMinutes} minutes
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Meeting Stats */}
          <div className="flex flex-wrap gap-3 justify-center">
            <Badge variant="secondary" className="text-sm px-4 py-2">
              <Clock className="h-4 w-4 mr-2" />
              {formatElapsedTime(elapsedTime)}
            </Badge>
            <Badge variant="secondary" className="text-sm px-4 py-2">
              <Users className="h-4 w-4 mr-2" />
              1 Participant
            </Badge>
            {devicesVerified && (
              <Badge variant="default" className="text-sm px-4 py-2 bg-green-500">
                <CheckCircle className="h-4 w-4 mr-2" />
                Devices Tested
              </Badge>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-center">
              <strong>Note:</strong> This is a placeholder meeting screen. In production, this would be your full meeting room with video, audio, chat, and all meeting features.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="w-full"
            >
              End Meeting
            </Button>
            <Button
              onClick={() => navigate("/create-meeting")}
              variant="ghost"
              className="w-full"
            >
              Create Another Meeting
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
